<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  appId: { type: [String, Number], required: true },
  // [{ id, key, label, group_type }] — current static/dynamic groups.
  groups: { type: Array, default: () => [] },
});

const emit = defineEmits(['changed']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const newName = ref('');
const isSaving = ref(false);

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function open() {
  newName.value = '';
  dialogRef.value?.open();
}

async function addGroup() {
  const name = newName.value.trim();
  if (!name) return;
  isSaving.value = true;
  try {
    await store.dispatch('appBuilder/createMemberGroup', {
      appId: props.appId,
      key: slugify(name),
      name,
      group_type: 'static',
    });
    newName.value = '';
    useAlert(t('APP_BUILDER.MEMBERS.GROUPS.ALERTS.CREATED'));
    emit('changed');
  } catch (error) {
    const status = error?.response?.status;
    useAlert(
      status === 422
        ? t('APP_BUILDER.MEMBERS.GROUPS.ALERTS.DUPLICATE')
        : t('APP_BUILDER.ALERTS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
}

async function renameGroup(group, event) {
  const name = event.target.value.trim();
  if (!name || name === group.label) return;
  try {
    await store.dispatch('appBuilder/updateMemberGroup', {
      appId: props.appId,
      id: group.id,
      name,
    });
    emit('changed');
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

async function deleteGroup(group) {
  try {
    await store.dispatch('appBuilder/deleteMemberGroup', {
      appId: props.appId,
      id: group.id,
    });
    useAlert(t('APP_BUILDER.MEMBERS.GROUPS.ALERTS.DELETED'));
    emit('changed');
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('APP_BUILDER.MEMBERS.GROUPS.TITLE')"
    :description="t('APP_BUILDER.MEMBERS.GROUPS.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('APP_BUILDER.MEMBERS.GROUPS.DONE')"
  >
    <div class="flex flex-col gap-4">
      <!-- Add a new static group -->
      <div class="flex items-end gap-2">
        <div class="flex-1">
          <Input
            v-model="newName"
            :label="t('APP_BUILDER.MEMBERS.GROUPS.NEW_LABEL')"
            :placeholder="t('APP_BUILDER.MEMBERS.GROUPS.NEW_PLACEHOLDER')"
            @keyup.enter="addGroup"
          />
        </div>
        <NextButton
          :label="t('APP_BUILDER.MEMBERS.GROUPS.ADD')"
          icon="i-lucide-plus"
          color="blue"
          size="sm"
          :disabled="!newName.trim()"
          :is-loading="isSaving"
          @click="addGroup"
        />
      </div>

      <!-- Existing groups -->
      <ul v-if="groups.length" class="flex flex-col gap-1.5">
        <li
          v-for="group in groups"
          :key="group.id || group.key"
          class="flex items-center gap-2 rounded-lg border border-n-weak px-2.5 py-1.5 bg-n-alpha-1"
        >
          <input
            :value="group.label || group.key"
            class="flex-1 min-w-0 bg-transparent text-sm text-n-slate-12 focus:outline-none"
            :aria-label="t('APP_BUILDER.MEMBERS.GROUPS.NEW_LABEL')"
            @blur="renameGroup(group, $event)"
            @keyup.enter="$event.target.blur()"
          />
          <span
            v-if="group.group_type === 'dynamic' || group.group_type === 1"
            class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 shrink-0"
          >
            {{ t('APP_BUILDER.MEMBERS.GROUPS.DYNAMIC') }}
          </span>
          <button
            type="button"
            class="shrink-0 text-n-slate-10 hover:text-red-500 inline-flex"
            :aria-label="t('APP_BUILDER.MEMBERS.GROUPS.DELETE')"
            @click="deleteGroup(group)"
          >
            <span class="i-lucide-trash-2 w-4 h-4" />
          </button>
        </li>
      </ul>
      <p v-else class="text-sm text-n-slate-10 text-center py-2">
        {{ t('APP_BUILDER.MEMBERS.GROUPS.EMPTY') }}
      </p>
    </div>
  </Dialog>
</template>
